import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react';

interface HJTrimTimelineProps {
  duration: number;
  currentTime: number;
  trimStart: number;
  trimEnd: number;
  onSeek: (time: number) => void;
  onTrimChange: (start: number, end: number) => void;
  isPlaying?: boolean;
}

type DragTarget = 'trimStart' | 'trimEnd' | 'playhead' | null;

const HANDLE_HIT_ZONE = 20;
const TRACK_HEIGHT = 28;

const fmtTime = (sec: number) => {
  if (!isFinite(sec)) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

/** Redesigned trim timeline matching the audition-enhancer reference:
 *   - Ruler ticks above the track (labelled every 20s, minor every 10s)
 *   - Selected region fills with brand blue
 *   - Faux waveform inside the selection for visual texture
 *   - Handles at each end with a floating IN/OUT timecode pill
 *   - Playhead line within the track
 *
 *  Interaction model identical to the existing TrimTimeline (pointer drag,
 *  three drag targets, touchmove prevent during drag). Existing TrimTimeline
 *  stays for legacy tools (Splitter etc.). */
export const HJTrimTimeline: React.FC<HJTrimTimelineProps> = ({
  duration,
  currentTime,
  trimStart,
  trimEnd,
  onSeek,
  onTrimChange,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragTarget, setDragTarget] = useState<DragTarget>(null);

  const total = Math.max(0.0001, duration);

  const clientXToTime = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track) return 0;
      const rect = track.getBoundingClientRect();
      const x = clientX - rect.left;
      const pct = Math.max(0, Math.min(1, x / rect.width));
      return pct * total;
    },
    [total]
  );

  const determineDragTarget = useCallback(
    (clientX: number): DragTarget => {
      const track = trackRef.current;
      if (!track) return 'playhead';
      const rect = track.getBoundingClientRect();
      const x = clientX - rect.left;
      const startPx = (trimStart / total) * rect.width;
      const endPx = (trimEnd / total) * rect.width;
      const distToStart = Math.abs(x - startPx);
      const distToEnd = Math.abs(x - endPx);
      if (distToStart < HANDLE_HIT_ZONE && distToStart <= distToEnd) return 'trimStart';
      if (distToEnd < HANDLE_HIT_ZONE) return 'trimEnd';
      return 'playhead';
    },
    [total, trimStart, trimEnd]
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      const target = determineDragTarget(e.clientX);
      setDragTarget(target);
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      const time = clientXToTime(e.clientX);
      if (target === 'trimStart') {
        const newStart = Math.max(0, Math.min(time, trimEnd - 0.1));
        onTrimChange(newStart, trimEnd);
        onSeek(newStart);
      } else if (target === 'trimEnd') {
        const newEnd = Math.min(total, Math.max(time, trimStart + 0.1));
        onTrimChange(trimStart, newEnd);
        onSeek(newEnd);
      } else {
        onSeek(Math.max(trimStart, Math.min(trimEnd, time)));
      }
    },
    [determineDragTarget, clientXToTime, trimStart, trimEnd, total, onTrimChange, onSeek]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragTarget) return;
      const time = clientXToTime(e.clientX);
      if (dragTarget === 'trimStart') {
        const newStart = Math.max(0, Math.min(time, trimEnd - 0.1));
        onTrimChange(newStart, trimEnd);
        onSeek(newStart);
      } else if (dragTarget === 'trimEnd') {
        const newEnd = Math.min(total, Math.max(time, trimStart + 0.1));
        onTrimChange(trimStart, newEnd);
        onSeek(newEnd);
      } else {
        onSeek(Math.max(trimStart, Math.min(trimEnd, time)));
      }
    },
    [dragTarget, clientXToTime, trimStart, trimEnd, total, onTrimChange, onSeek]
  );

  const handlePointerUp = useCallback(() => setDragTarget(null), []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const prevent = (e: TouchEvent) => {
      if (dragTarget) e.preventDefault();
    };
    track.addEventListener('touchmove', prevent, { passive: false });
    return () => track.removeEventListener('touchmove', prevent);
  }, [dragTarget]);

  // Ticks: minor every 10s, labelled every 20s + endpoint
  const ticks = useMemo(() => {
    const arr: number[] = [];
    if (total <= 0) return arr;
    const step = total <= 30 ? 5 : total <= 120 ? 10 : 30;
    for (let t = 0; t <= total; t += step) arr.push(t);
    if (arr[arr.length - 1] !== Math.round(total)) arr.push(Math.round(total));
    return arr;
  }, [total]);
  const labelStep = total <= 30 ? 10 : total <= 120 ? 20 : 60;
  const isLabelled = (t: number) => t === 0 || t === Math.round(total) || t % labelStep === 0;

  // Faux waveform — deterministic, avoids drawing actual audio
  const waveform = useMemo(() => {
    const bars: number[] = [];
    const N = 56;
    for (let i = 0; i < N; i++) {
      const h = 0.25 + (0.55 * Math.abs(Math.sin(i * 0.5) + Math.cos(i * 0.12))) / 2;
      bars.push(h);
    }
    return bars;
  }, []);

  const startPct = (trimStart / total) * 100;
  const endPct = (trimEnd / total) * 100;
  const playheadPct = (currentTime / total) * 100;

  return (
    <div className="select-none touch-none" style={{ width: '100%' }}>
      {/* Ruler */}
      <div
        className="relative"
        style={{ height: 14, marginBottom: 4 }}
        aria-hidden
      >
        {ticks.filter(isLabelled).map((t) => (
          <div
            key={t}
            className="tabular"
            style={{
              position: 'absolute',
              left: `${(t / total) * 100}%`,
              transform: 'translateX(-50%)',
              fontFamily: 'var(--hj-font-mono)',
              fontSize: 9,
              color: 'var(--hj-ink-4)',
              letterSpacing: '0.04em',
              whiteSpace: 'nowrap',
            }}
          >
            {fmtTime(t)}
          </div>
        ))}
      </div>

      {/* Track */}
      <div
        ref={trackRef}
        className="relative"
        style={{
          height: TRACK_HEIGHT + 16,
          cursor: dragTarget ? 'grabbing' : 'pointer',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {/* Base track */}
        <div
          className="absolute left-0 right-0 rounded"
          style={{
            top: 8,
            height: TRACK_HEIGHT,
            background: 'var(--hj-surface-3)',
          }}
        />
        {/* Tick marks on the track */}
        {ticks.map((t) => (
          <div
            key={t}
            aria-hidden
            style={{
              position: 'absolute',
              left: `${(t / total) * 100}%`,
              top: 4,
              height: 4,
              width: 1,
              background: 'var(--hj-ink-4)',
              opacity: 0.5,
              transform: 'translateX(-0.5px)',
            }}
          />
        ))}
        {/* Selected region */}
        <div
          className="absolute rounded pointer-events-none"
          style={{
            top: 8,
            height: TRACK_HEIGHT,
            left: `${startPct}%`,
            right: `${100 - endPct}%`,
            background: 'var(--hj-blue)',
            opacity: 0.92,
          }}
        />
        {/* Faux waveform inside selection */}
        <div
          className="absolute flex items-center pointer-events-none"
          style={{
            top: 8,
            height: TRACK_HEIGHT,
            left: `${startPct}%`,
            right: `${100 - endPct}%`,
            padding: '0 4px',
            gap: 1,
          }}
        >
          {waveform.map((h, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: `${h * 100}%`,
                background: 'oklch(0.16 0.005 70 / 0.45)',
                borderRadius: 1,
              }}
            />
          ))}
        </div>

        {/* Handles */}
        <Handle pct={startPct} label="IN" timeLabel={fmtTime(trimStart)} />
        <Handle pct={endPct} label="OUT" timeLabel={fmtTime(trimEnd)} right />

        {/* Playhead */}
        <div
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            left: `${playheadPct}%`,
            top: 4,
            width: 2,
            height: TRACK_HEIGHT + 8,
            background: 'var(--hj-ink)',
            transform: 'translateX(-1px)',
            boxShadow: '0 0 4px oklch(0 0 0 / 0.5)',
            zIndex: 10,
          }}
        />
      </div>
    </div>
  );
};

const Handle: React.FC<{
  pct: number;
  label: string;
  timeLabel: string;
  right?: boolean;
}> = ({ pct, label, timeLabel, right }) => {
  return (
    <div
      className="absolute pointer-events-none"
      style={{
        top: 0,
        bottom: 0,
        left: `calc(${pct}% - 6px)`,
        width: 12,
      }}
    >
      {/* Grip bar */}
      <div
        style={{
          width: 12,
          height: TRACK_HEIGHT + 8,
          marginTop: 4,
          background: 'var(--hj-blue)',
          borderRadius: 3,
          boxShadow: '0 1px 0 oklch(0 0 0 / 0.4), inset 0 0 0 1px oklch(0 0 0 / 0.25)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            style={{
              width: 4,
              height: 1.5,
              background: 'oklch(0.16 0.005 70 / 0.55)',
              borderRadius: 1,
            }}
          />
        ))}
      </div>
      {/* Floating timecode pill */}
      <div
        className="tabular"
        style={{
          position: 'absolute',
          bottom: -4,
          [right ? 'right' : 'left']: 16,
          whiteSpace: 'nowrap',
          padding: '2px 6px',
          borderRadius: 3,
          background: 'var(--hj-blue)',
          color: 'oklch(0.16 0.005 70)',
          fontFamily: 'var(--hj-font-mono)',
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: '0.06em',
        }}
      >
        {label} {timeLabel}
      </div>
    </div>
  );
};
